import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { mindtimeWazuhFileMonitoringApi } from 'src/api/mindtime/wazuhFileMonitoringApi';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type {
  WazuhFimEventItem,
  WazuhFimEventsQuery,
  WazuhFimSummaryData,
} from 'src/types/mindtime/wazuhFileMonitoring';
import type { WazuhPagedData, WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';
import { buildEdrApiScope } from '../shared/wazuhScopeParams';

export type FileMonitoringFilters = {
  changeType?: string;
  severityBand?: string;
  ruleLevel?: string;
  wazuhAgentId?: string;
  organizationId?: string;
};

export type FileMonitoringState = {
  loading: boolean;
  summary: WazuhFimSummaryData | null;
  items: WazuhPagedData<WazuhFimEventItem> | null;
};

type BuildFileMonitoringQueryInput = {
  scope: WazuhScopeParams;
  profileOrgId?: string;
  isReseller: boolean;
  search?: string;
  filters?: FileMonitoringFilters;
  page?: number;
  pageSize?: number;
};

const emptyState = (): FileMonitoringState => ({
  loading: true,
  summary: null,
  items: null,
});

const buildFileMonitoringQuery = ({
  scope,
  profileOrgId,
  isReseller,
  search = '',
  filters = {},
  page,
  pageSize,
}: BuildFileMonitoringQueryInput): WazuhFimEventsQuery => {
  const { changeType, severityBand, ruleLevel, wazuhAgentId, organizationId } = filters;
  const effectiveScope = buildEdrApiScope({
    scope,
    profileOrgId,
    isReseller,
    pageOrganizationId: organizationId || undefined,
  });

  return {
    ...effectiveScope,
    ...(typeof page === 'number' ? { page } : {}),
    ...(typeof pageSize === 'number' ? { pageSize } : {}),
    ...(search ? { search } : {}),
    ...(changeType ? { changeType } : {}),
    ...(severityBand ? { severityBand } : {}),
    ...(ruleLevel ? { ruleLevel } : {}),
    ...(wazuhAgentId ? { wazuhAgentId } : {}),
  };
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const useFileMonitoringExport = (
  search = '',
  filters: FileMonitoringFilters = {}
) => {
  const { scope, isReseller } = useWazuhScope();
  const { userData } = useUserProfile();
  const profileOrgId = userData?.organization?.id;
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(() => {
    setExporting(true);
    mindtimeWazuhFileMonitoringApi
      .export(buildFileMonitoringQuery({ scope, profileOrgId, isReseller, search, filters }))
      .then(({ blob, filename }) => {
        const name = filename?.trim() ? filename : 'file_monitoring_export.xlsx';
        triggerBlobDownload(blob, name);
        toast.success('Export downloaded');
      })
      .catch((error) => {
        logger(error, 'error', 'useFileMonitoringExport');
        toast.error(error instanceof Error ? error.message : 'Export failed');
      })
      .finally(() => {
        setExporting(false);
      });
  }, [scope, profileOrgId, isReseller, search, filters]);

  return { exporting, handleExport };
};

export const useFileMonitoringData = (
  page = 0,
  pageSize = 10,
  search = '',
  filters: FileMonitoringFilters = {}
): FileMonitoringState => {
  const { scope, isReseller } = useWazuhScope();
  const { userData } = useUserProfile();
  const profileOrgId = userData?.organization?.id;
  const [state, setState] = useState<FileMonitoringState>(emptyState);

  const { changeType, severityBand, ruleLevel, wazuhAgentId, organizationId } = filters;

  useEffect(() => {
    let cancelled = false;

    mindtimeWazuhFileMonitoringApi
      .getSummary(
        buildEdrApiScope({
          scope,
          profileOrgId,
          isReseller,
          pageOrganizationId: organizationId || undefined,
        })
      )
      .then((summary) => {
        if (cancelled) return;
        setState((prev) => ({ ...prev, summary }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useFileMonitoringData.summary');
          setState((prev) => ({ ...prev, summary: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [scope, profileOrgId, isReseller, organizationId]);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    mindtimeWazuhFileMonitoringApi
      .getEvents(
        buildFileMonitoringQuery({
          scope,
          profileOrgId,
          isReseller,
          search,
          filters,
          page,
          pageSize,
        })
      )
      .then((items) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          items,
        }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useFileMonitoringData.events');
          setState((prev) => ({
            ...prev,
            loading: false,
            items: null,
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    page,
    pageSize,
    search,
    changeType,
    severityBand,
    ruleLevel,
    wazuhAgentId,
    organizationId,
  ]);

  return state;
};
